import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useAuthorRedirect } from "@/hooks/useAuthorRedirect";

interface CTAButtonProps {
  to?: string;
  href?: string; // Add href property for external links
  variant?: "default" | "secondary" | "outline";
  size?: "default" | "sm" | "lg" | "icon";
  external?: boolean;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void; // Add onClick handler
  customRedirect?: 'author'; // 添加customRedirect属性，目前仅支持'author'
}

const CTAButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, CTAButtonProps>(
  (
    {
      to,
      href,
      variant = "default",
      size = "default",
      external = false,
      icon,
      className,
      children,
      onClick,
      customRedirect,
    },
    ref
  ) => {
    const { goToAuthorPlatform } = useAuthorRedirect();
    
    const buttonContent = (
      <>
        {icon && <span className="mr-2">{icon}</span>}
        {children}
      </>
    );

    // 如果有customRedirect，创建自定义处理函数
    const handleCustomRedirect = () => {
      if (customRedirect === 'author') {
        goToAuthorPlatform();
      }
      if (onClick) {
        onClick();
      }
    };

    // Determine the URL to use (prefer href for external links if provided)
    const url = external ? (href || to) : to;

    // 如果有customRedirect，优先使用customRedirect跳转
    if (customRedirect) {
      return (
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="inline-block"
        >
          <Button
            variant={variant}
            size={size}
            onClick={handleCustomRedirect}
            className={cn("flex items-center justify-center", className)}
            ref={ref as React.Ref<HTMLButtonElement>}
          >
            {buttonContent}
          </Button>
        </motion.div>
      );
    }

    // If onClick is provided and no external/to/href, use a regular button
    if (onClick && !external && !to && !href) {
      return (
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="inline-block"
        >
          <Button
            variant={variant}
            size={size}
            onClick={onClick}
            className={cn("flex items-center justify-center", className)}
            ref={ref as React.Ref<HTMLButtonElement>}
          >
            {buttonContent}
          </Button>
        </motion.div>
      );
    }

    return (
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className="inline-block" // Added inline-block to contain the button size
      >
        {external ? (
          <Button
            variant={variant}
            size={size}
            asChild
            className={cn("flex items-center justify-center", className)}
            onClick={onClick}
          >
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              ref={ref as React.Ref<HTMLAnchorElement>}
            >
              {buttonContent}
            </a>
          </Button>
        ) : (
          <Button
            variant={variant}
            size={size}
            asChild
            className={cn("flex items-center justify-center", className)}
          >
            <Link 
              to={url || ""} 
              onClick={onClick}
              ref={ref as React.Ref<HTMLAnchorElement>}
            >
              {buttonContent}
            </Link>
          </Button>
        )}
      </motion.div>
    );
  }
);

CTAButton.displayName = "CTAButton";

export default CTAButton;
